import React from "react";
import { useSelector } from "react-redux";
import { Col, Row } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useDispatch } from "react-redux";
import Messageitem from "./components/messageitem";
import MessageArea from "./components/messageArea";
import { Chat } from "../../../api/chat/chat-interface";
import {
  addMessage,
  markAsRead,
  selectChats,
  setCurrentChat,
  setCurrentChatType,
} from "../../../store/chatSlice/chatSlice";
import { selectChatSocket } from "../../../store/webSocketSlice/webSocketSlice";
import ChatSearch from "./components/chatSearch";

type UserType = "Students" | "Parents" | "Teachers";

const userTypes: UserType[] = ["Students", "Parents", "Teachers"];

const Messaging = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    chats: chatRooms,
    currentChat,
    currentChatType,
  } = useSelector(selectChats);
  const chatSocket = useSelector(selectChatSocket);

  const { _id: userId } = useSelector((state: any) => state?.user?.user);

  // hooks
  React.useEffect(() => {
    navigate("/student/dashboard/messaging", {
      state: {
        pageName: "Messaging",
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handlers
  const handleSetCurrentChat = (room: Chat) => {
    dispatch(setCurrentChat(room));

    // mark as read
    dispatch(
      markAsRead({
        chatId: room._id,
        userType: currentChatType,
        userId,
      })
    );

    // set current chat
    setCurrentChat(room);

    
    // send read message via websocket if the last message is not read by the current user
    if (
      !room?.conversations
      ?.slice(-1)[0]
      ?.readBy?.map((item: any) => item.userId)
      ?.includes(userId)
      ) {
      chatSocket?.emit("mark_chat_messages_as_read", {
        chatId: room._id,
        userId,
      });
    }
  };

  const handleGetLastMessage = (room: Chat): string => {
    return room?.conversations?.length
      ? room?.conversations.slice(-1)[0]?.body
      : "No new message";
  };

  // state
  const sendMessage = React.useCallback(
    (content: string) => {
      // add message to current chat
      if (!content) {
        return;
      }

      const messageObj = {
        identifier: moment().format("x"),
        body: content,
        creatorId: userId,
        chatId: currentChat?._id || "",
        userType: currentChatType,
      };

      dispatch(addMessage(messageObj));

      // send message via websocket
      chatSocket?.emit("send_message", messageObj);
    },
    [chatSocket, currentChat, currentChatType, dispatch, userId]
  );

  return (
    <Row
      className="overflow-hidden md:mr-10"
      style={
        {
          // height: "calc(100vh - 10rem)",
        }
      }
    >
      <Col xs={24} md={24} lg={9}>
        <div className="grid gap-4">
          <div className="flex">
            {userTypes.map((item) => (
              <p
                key={`user-type-${item}`}
                onClick={() => {
                  dispatch(setCurrentChatType(item));
                }}
                className={`mr-2 w-fit cursor-pointer rounded-[10px] px-[0.6rem] py-[0.6rem] text-[18px] leading-[28px] text-appcolorblack opacity-[0.6] ${
                  currentChatType === item
                    ? "bg-appcolorgreyfaintthree font-bold"
                    : ""
                } hover:bg-appcolorpurplefaint hover:text-appcolorpurpleseight`}
              >
                {item}
              </p>
            ))}
          </div>

          <ChatSearch/>

          <div className="scrollable">
            {chatRooms[currentChatType]?.length ? (
              chatRooms[currentChatType].map((room) => {
                return (
                  <div
                    key={room._id}
                    onClick={() => handleSetCurrentChat(room)}
                  >
                    <Messageitem
                      updatedAt={
                        room?.conversations?.slice(-1)[0]?.dateSent ||
                        room?.createdAt
                      }
                      lastMessage={handleGetLastMessage(room)}
                      userName={
                        room.members.find(
                          (member) => member?.userId?._id !== userId
                        )?.userId?.userName || ""
                      }
                      hasSeen={!room?.conversations ? true : room?.conversations
                        ?.slice(-1)[0]
                        ?.readBy?.map((item: any) => item.userId)
                        ?.includes(userId)}
                    />
                  </div>
                );
              })
            ) : (
              <p className="py-2 font-semibold">No new chats</p>
            )}
          </div>
        </div>
      </Col>
      <Col xs={24} md={24} lg={15} className="h-full">
        <MessageArea
          conversation={currentChat?.conversations || []}
          receiver={
            currentChat?.members.find((member) => member.userId?._id !== userId)
              ?.userId || null
          }
          sendMessage={sendMessage}
        />
      </Col>
    </Row>
  );
};

export default Messaging;
